import { accountTypeLabel as productAccountTypeLabel, verificationStatusLabel as productVerificationStatusLabel } from "@/src/lib/accountPresentation";
import { getSubjectLabel, getTrackLabel } from "@/src/lib/taxonomy";
import type {
  AccountType,
  IdentityRequestKind,
  IdentityRequestStatus,
  VerificationStatus,
} from "@/src/types/identity";

/**
 * Control Center is allowed to receive backend enum values, but it should not
 * make operators read those values as if they were product copy.
 */
export function humanizeEnum(value: unknown, fallback = "Non précisé") {
  if (typeof value !== "string" || !value.trim()) return fallback;
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function controlAccountTypeLabel(value: AccountType | string | null | undefined) {
  if (value === "student" || value === "teacher" || value === "institution") {
    return productAccountTypeLabel(value);
  }
  return "Type de compte non précisé";
}

export function controlVerificationStatusLabel(value: VerificationStatus | string | null | undefined) {
  if (value === "unverified" || value === "pending" || value === "verified" || value === "rejected") {
    return productVerificationStatusLabel(value);
  }
  return "Non précisé";
}

export function identityRequestKindLabel(kind: IdentityRequestKind | string | null | undefined) {
  if (kind === "teacher_verification") return "Vérification professeur";
  if (kind === "institution_identity" || kind === "institution_conversion") return "Identité institutionnelle";
  return "Demande d’identité";
}

export function identityRequestStatusLabel(status: IdentityRequestStatus | string | null | undefined) {
  if (status === "pending") return "En attente";
  if (status === "approved") return "Approuvée";
  if (status === "rejected") return "Refusée";
  if (status === "cancelled") return "Annulée";
  return humanizeEnum(status);
}

export function moderationCaseStatusLabel(status: string | null | undefined) {
  if (status === "open") return "Ouvert";
  if (status === "reviewing") return "En cours";
  if (status === "resolved") return "Résolu";
  if (status === "dismissed") return "Classé";
  return humanizeEnum(status);
}

export function moderationSeverityLabel(severity: string | null | undefined) {
  if (severity === "normal") return "Normale";
  if (severity === "high") return "Élevée";
  if (severity === "critical") return "Critique";
  return humanizeEnum(severity, "Normale");
}

export function moderationTargetLabel(targetType: string | null | undefined, identityType?: string | null) {
  if (targetType === "post") return "Publication";
  if (targetType === "comment") return "Commentaire";
  if (targetType === "user" && identityType === "institution") return "Identité institutionnelle";
  if (targetType === "user" && identityType) return "Identité personnelle";
  if (targetType === "user") return "Profil";
  if (targetType === "moderationCase") return "Dossier de modération";
  if (targetType === "identityRequest") return "Demande d’identité";
  if (targetType === "sponsoredCampaign") return "Campagne sponsorisée";
  if (targetType === "controlMember") return "Membre Control";
  return humanizeEnum(targetType, "Cible");
}

export function moderationReasonLabel(value: string | null | undefined) {
  const labels: Record<string, string> = {
    sexual_content: "Contenu sexuel",
    minor_safety: "Protection des mineurs",
    violence: "Violence",
    harassment: "Harcèlement",
    hate: "Haine ou abus",
    spam: "Spam",
    impersonation: "Usurpation d’identité",
    off_topic: "Hors sujet",
    privacy: "Vie privée",
    other: "Autre",
  };
  return labels[value || ""] || humanizeEnum(value, "Non précisée");
}

export function moderationSourceLabel(source: string | null | undefined) {
  if (source === "automatic") return "Détection automatique";
  if (source === "user") return "Signalement utilisateur";
  if (source === "control_center") return "Control Center";
  if (source === "system") return "Système";
  if (source === "bootstrap") return "Initialisation";
  return humanizeEnum(source, "Source non précisée");
}

export function moderationContentStatusLabel(status: string | null | undefined) {
  if (status === "pending") return "En attente";
  if (status === "approved") return "Approuvé";
  if (status === "rejected") return "Refusé";
  if (status === "removed") return "Retiré";
  if (status === "quarantined") return "Mis en quarantaine";
  return humanizeEnum(status);
}

export function moderationReportStatusLabel(status: string | null | undefined) {
  if (status === "open") return "Ouvert";
  if (status === "reviewed") return "Examiné";
  if (status === "resolved") return "Traité";
  if (status === "dismissed") return "Classé";
  if (status === "recorded") return "Enregistré";
  return humanizeEnum(status);
}

export function moderationAccountStatusLabel(status: string | null | undefined) {
  if (status === "active") return "Actif";
  if (status === "restricted") return "Restreint";
  if (status === "suspended") return "Suspendu";
  if (status === "banned") return "Banni";
  return humanizeEnum(status);
}

export function moderationActionLabel(value: string | null | undefined, durationDays?: 1 | 7) {
  if (value === "dismiss" || value === "no_action") return "Classer sans suite";
  if (value === "remove_content" || value === "content_removed") return "Retirer le contenu";
  if (value === "warning") return "Avertissement";
  if (value === "posting_restricted") return `Restriction temporaire${durationDays ? ` · ${durationDays} jour${durationDays > 1 ? "s" : ""}` : ""}`;
  if (value === "temporary_suspension") return `Suspension${durationDays ? ` · ${durationDays} jour${durationDays > 1 ? "s" : ""}` : ""}`;
  if (value === "permanent_ban") return "Bannissement";
  if (value === "content_approved") return "Approuver le contenu";
  return humanizeEnum(value, "Action non précisée");
}

export function controlRoleLabel(role: string | null | undefined) {
  if (role === "owner") return "Propriétaire";
  if (role === "admin") return "Administrateur";
  if (role === "operator") return "Opérateur";
  if (role === "viewer") return "Lecteur";
  return humanizeEnum(role, "Rôle non précisé");
}

export function controlMemberStatusLabel(status: string | null | undefined) {
  if (status === "active") return "Actif";
  if (status === "disabled") return "Désactivé";
  return humanizeEnum(status, "Statut non précisé");
}

export function campaignStatusLabel(status: string | null | undefined) {
  if (status === "draft") return "Brouillon";
  if (status === "active") return "Active";
  if (status === "paused") return "En pause";
  if (status === "ended") return "Terminée";
  return humanizeEnum(status);
}

export function campaignModerationStatusLabel(status: string | null | undefined) {
  if (status === "pending") return "En attente de validation";
  if (status === "approved") return "Approuvée";
  if (status === "rejected") return "Rejetée";
  return humanizeEnum(status);
}

export function campaignDeliveryStateLabel(status: string | null | undefined) {
  if (status === "draft") return "Préparation";
  if (status === "paused") return "En pause";
  if (status === "blocked") return "Bloquée";
  if (status === "expired") return "Expirée";
  if (status === "scheduled") return "Programmée";
  if (status === "delivering") return "En diffusion";
  if (status === "ended") return "Terminée";
  return humanizeEnum(status);
}

export function auditActionLabel(action: string | null | undefined) {
  const labels: Record<string, string> = {
    "identity.request.created": "Demande d’identité créée",
    "identity.request.cancelled": "Demande d’identité annulée",
    "identity.request.decided": "Demande d’identité traitée",
    "control.member.created": "Membre Control ajouté",
    "control.member.updated": "Accès membre mis à jour",
    "control.member.disabled": "Membre Control désactivé",
    "control.member.bootstrap_created": "Membre Control initialisé",
    "control.member.bootstrap_updated": "Membre Control initialisé",
    "moderation.case.reviewing": "Dossier pris en charge",
    "monetization.campaign.created": "Campagne créée",
    "monetization.campaign.updated": "Campagne modifiée",
    "monetization.campaign.active": "Campagne activée",
    "monetization.campaign.paused": "Campagne mise en pause",
    "monetization.campaign.ended": "Campagne terminée",
    "monetization.campaign.approved": "Campagne approuvée",
    "monetization.campaign.rejected": "Campagne rejetée",
  };
  if (action && labels[action]) return labels[action];
  if (action?.startsWith("moderation.")) return `Modération · ${moderationActionLabel(action.slice("moderation.".length))}`;
  return humanizeEnum(action, "Événement enregistré");
}

export function auditSourceLabel(source: string | null | undefined) {
  return moderationSourceLabel(source);
}

export function toControlDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number" && Number.isFinite(value)) {
    const millis = Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
    const date = new Date(millis);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string" && value.trim()) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (!value || typeof value !== "object") return null;

  const record = value as Record<string, unknown>;
  if (typeof record.toDate === "function") {
    try {
      const date = record.toDate();
      if (date instanceof Date && !Number.isNaN(date.getTime())) return date;
    } catch {
      // Fall through to the serialized Timestamp shape.
    }
  }

  const seconds = typeof record.seconds === "number"
    ? record.seconds
    : typeof record._seconds === "number"
      ? record._seconds
      : null;
  if (seconds !== null && Number.isFinite(seconds)) {
    const nanoseconds = typeof record.nanoseconds === "number"
      ? record.nanoseconds
      : typeof record._nanoseconds === "number"
        ? record._nanoseconds
        : 0;
    const date = new Date(seconds * 1000 + nanoseconds / 1_000_000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function formatControlDate(value: unknown, fallback = "Date non renseignée") {
  const date = toControlDate(value);
  return date
    ? date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : fallback;
}

export function formatControlDateOnly(value: unknown, fallback = "Date non renseignée") {
  const date = toControlDate(value);
  return date
    ? date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : fallback;
}

export function formatSubjectList(values: readonly string[]) {
  return values.length ? values.map(getSubjectLabel).join(", ") : "Toutes les matières";
}

export function formatTrackList(values: readonly string[]) {
  return values.length ? values.map(getTrackLabel).join(", ") : "Toutes les filières";
}
